/* eslint-disable max-len */
import {
  TextField,
  useTranslate,
  ReferenceField,
  useGetOne,
  SortButton,
  FilterButton,
  FilterForm,
  SelectInput,
  ReferenceInput,
  LinkToType,
  RaRecord,
  Identifier,
  useListContext
} from "react-admin";
import * as User from "providers/user";
import React, { useMemo, useState } from "react";
import { DateRange } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import {
  Badge,
  ListItemButton,
  Popover,
  Theme,
  useMediaQuery
} from "@mui/material";
import { FilterIcon, SortIcon } from "assets/Icons";
import { useSearchParams } from "react-router-dom";
import CustomList from "./CustomList";
import AppointmentDatesSelect from "./AppointmentDatesSelect";
import AppointmentDatesSelectButton from "./AppointmentDatesSelectButton";
import CustomAppointmentListContent from "./CustomAppointmentListContent";
import MobileFilterModal from "./MobileFilterModal";
import { CustomButton } from "./CustomButton";
import { muiColors } from "themes/colors";
import { ICustomSort } from "types";

export enum FunctionFieldTypes {
  SERVICE = "service",
  ARTIST = "artist"
}

const customSortChoices: ICustomSort[] = [
  { field: "start_time", order: "DESC" },
  { field: "start_time", order: "ASC" },
  { field: "end_time", order: "DESC" },
  { field: "end_time", order: "ASC" }
];

const CheckedInFilter = ({ source, label }: any) => {
  return (
    <SelectInput
      source={source}
      label={label}
      choices={[
        { name: "No", id: JSON.stringify({ equals: null }) },
        { name: "Yes", id: JSON.stringify({ gte: "2023-07-12T00:00:00.000Z" }) }
      ]}
      format={(c) => (c === '{"equals":null}' ? c : JSON.stringify(c))}
      parse={(v) => (v === '{"equals":null}' ? v : JSON.parse(v))}
    />
  );
};
const CancelledFilter = ({ source, label }: any) => {
  return (
    <SelectInput
      source={source}
      label={label}
      choices={[
        { name: "No", id: JSON.stringify({ equals: null }) },
        { name: "Yes", id: JSON.stringify({ gt: "2020-07-12T00:00:00.000Z" }) }
      ]}
      format={(c) => (c === '{"equals":null}' ? c : JSON.stringify(c))}
      parse={(v) => (v === '{"equals":null}' ? v : JSON.parse(v))}
      defaultValue={{ gt: "2020-07-12T00:00:00.000Z" }}
      defaultChecked={true}
      emptyText=""
      emptyValue="{}"
    />
  );
};

const LocationFilter = ({ source, label }: any) => {
  const { permissions } = User.useUser();
  return (
    <ReferenceInput
      source={source}
      reference="Location"
      label={label}
      filter={{ company_id: permissions.companyId }}
    >
      <SelectInput
        optionText={(value) => value.nickname || value.name}
        format={(value) => (!value ? null : value)}
        label={label}
      />
    </ReferenceInput>
  );
};

const CustomAppointmentListActions = ({
  title,
  dateSelector,
  selectToday
}: {
  title?: string;
  dateSelector: any;
  selectToday: () => void;
}) => {
  const translate = useTranslate();
  const { user } = User.useUser();
  const { filterValues, setFilters, setSort, sort } = useListContext();
  const filtersCount =
    (filterValues?.location_id ? 1 : 0) +
    (filterValues?.checked_in_at ? 1 : 0) +
    (filterValues?.cancelled_at ? 1 : 0);
  const customFilters = [
    <CheckedInFilter
      source="checked_in_at"
      label={translate("appointment.checked_in")}
    />,
    <LocationFilter
      source="location_id"
      label={translate("appointment.location")}
    />,
    <CancelledFilter
      source="cancelled_at"
      label={translate("appointment.cancelled")}
      defaultValue={{ gt: "2020-07-12T00:00:00.000Z" }}
      defaultChecked={true}
    />
  ];

  const customMobileFilters = [
    <CheckedInFilter
      source="checked_in_at"
      label={translate("appointment.checked_in")}
      alwaysOn={true}
    />,
    <LocationFilter
      source="location_id"
      label={translate("appointment.location")}
      alwaysOn={true}
    />,
    <CancelledFilter
      source="cancelled_at"
      label={translate("appointment.cancelled")}
      alwaysOn={true}
    />
  ];

  const isDefaultSort = sort?.field === "start_time" && sort?.order === "DESC";
  const [isLocationMobileFilterModalOpen, setIsLocationMobileFilterModalOpen] =
    useState<boolean>(false);
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen
    ? "appointment-list-sort-actions-popover"
    : undefined;

  const handleSortButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  if (isSmall) {
    return (
      <div
        className={`w-full min-h-[80px] pb-2 ${
          user.impersonate_company_id ? "mt-[30px]" : ""
        } bg-mono-primaryBgColor`}
      >
        {title ? (
          <div className="flex justify-between items-center px-4 gap-2 mt-[12px] mb-1">
            <h4 className="font-['OptimaLTStd'] font-[600]">{title}</h4>
            <CustomButton
              onClick={() => {
                if (!isDefaultSort) {
                  setSort({ field: "start_time", order: "DESC" });
                }
                if (!!filterValues?.start_time && !!filterValues?.end_time) {
                  setFilters(
                    {
                      start_time: filterValues.start_time,
                      end_time: filterValues.end_time
                    },
                    undefined,
                    false
                  );
                } else {
                  setFilters({}, undefined, false);
                }
              }}
              variant="tertiary"
              text={translate("common.reset_all")}
              sx={{ color: muiColors.error }}
              disabled={filtersCount === 0 && isDefaultSort}
            />
          </div>
        ) : (
          <div />
        )}
        <div className="w-full h-[50px] overflow-x-hidden relative">
          <div className="absolute w-full h-full bottom-0 left-0 overflow-x-auto hide-scroll-bar">
            <div className="px-4 flex gap-[8px] w-max items-center h-full">
              {dateSelector}
              <CustomButton
                onClick={selectToday}
                variant="secondary"
                text={translate("common.today")}
              />
              <Badge
                color="error"
                badgeContent={isDefaultSort ? 0 : 1}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                sx={{
                  "& .MuiBadge-badge": {
                    top: "6px",
                    left: "6px"
                  }
                }}
              >
                <div>
                  <CustomButton
                    onClick={handleSortButtonClick}
                    startIcon={<SortIcon />}
                    variant="secondary"
                    text={`${translate("common.sort_by")}: ${translate(
                      `common.${sort.field}`
                    )} - ${sort?.order}`}
                    sx={{
                      backgroundColor: "#FAF9F7",
                      fontSize: "12px",
                      textTransform: "capitalize"
                    }}
                  />
                </div>
              </Badge>
              <Badge
                color="error"
                badgeContent={filtersCount}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                sx={{
                  "& .MuiBadge-badge": {
                    top: "6px",
                    left: "6px"
                  }
                }}
              >
                <div>
                  <CustomButton
                    onClick={() => setIsLocationMobileFilterModalOpen(true)}
                    startIcon={<FilterIcon />}
                    variant="secondary"
                    text={translate("common.add_filters")}
                    sx={{ backgroundColor: "#FAF9F7", fontSize: "12px" }}
                  />
                </div>
              </Badge>
            </div>
          </div>
        </div>
        {isLocationMobileFilterModalOpen && (
          <MobileFilterModal
            open={isLocationMobileFilterModalOpen}
            onClose={() => setIsLocationMobileFilterModalOpen(false)}
            title={translate("common.add_filters")}
          >
            <div className="flex flex-col items-center">
              <FilterForm filters={customMobileFilters} />
            </div>
          </MobileFilterModal>
        )}
        <Popover
          id={id}
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          elevation={4}
          sx={{
            "& .MuiPopover-paper": {
              backgroundColor: "#fff",
              borderRadius: "4px",
              textTransform: "capitalize"
            }
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          {customSortChoices
            .filter(
              (curr) => curr.field !== sort?.field || curr.order !== sort.order
            )
            ?.map((item) => (
              <ListItemButton
                onClick={() => {
                  setSort({ field: item.field, order: item.order });
                  setAnchorEl(null);
                }}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter",
                  "& .MuiListItemButton-root": {
                    fontSize: "14px",
                    fontFamily: "Inter"
                  }
                }}
              >
                {translate(`common.${item.field}`) + " - " + item.order}
              </ListItemButton>
            ))}
        </Popover>
      </div>
    );
  }

  return (
    <div
      className={`w-full min-h-[80px] ${
        user.impersonate_company_id ? "mt-[30px]" : ""
      } flex justify-between items-center bg-mono-primaryBgColor whitespace-nowrap flex-wrap pr-4 xs:px-0`}
    >
      {title ? (
        <div className="flex justify-between items-center gap-[16px]">
          <h4 className="font-['OptimaLTStd'] text-[20px] font-[600]">
            {title}
          </h4>
          <FilterForm filters={customFilters} />
        </div>
      ) : (
        <div />
      )}
      <div className="flex gap-[8px] items-center customListActionsButtonsWrapper">
        <CustomButton
          onClick={selectToday}
          variant="secondary"
          text={translate("common.today")}
        />
        <FilterButton filters={customFilters} disableSaveQuery />
        <SortButton
          fields={["start_time", "end_time"]}
          label={translate("common.sort_by")}
        />
        {dateSelector}
      </div>
    </div>
  );
};

export default function AppointmentList() {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const [searchParams] = useSearchParams();

  const localStorageAppointmentListParams = localStorage.getItem(
    "RaStore.Appointment.listParams"
  );
  const parsedLocalStorageAppointmentListParams =
    localStorageAppointmentListParams &&
    JSON.parse(localStorageAppointmentListParams);

  const isFilteredByLocation = useMemo(
    () =>
      Boolean(
        (searchParams.get("filter") &&
          JSON.parse(searchParams.get("filter") || "{}").location_id) ||
          parsedLocalStorageAppointmentListParams?.filter?.location_id
      ),
    [searchParams, parsedLocalStorageAppointmentListParams?.filter?.location_id]
  );
  const [dateValue, setDateValue] = useState<DateRange<Dayjs>>([
    parsedLocalStorageAppointmentListParams?.filter?.start_time
      ? dayjs(
          parsedLocalStorageAppointmentListParams?.filter?.start_time
        )?.startOf("day")
      : dayjs()?.startOf("day"),
    parsedLocalStorageAppointmentListParams?.filter?.end_time
      ? dayjs(parsedLocalStorageAppointmentListParams?.filter?.end_time)?.endOf(
          "day"
        )
      : dayjs().add(7, "day")?.endOf("day")
  ]);

  // parsedLocalStorageAppointmentListParams?.filter?.location_id ||
  // should be written in both places,
  // if we write it outside ternary operator it will not trigger
  const locationIds = permissions.isArtist
    ? parsedLocalStorageAppointmentListParams?.filter?.location_id ||
      user?.joined_company?.locations?.map((location: any) => location.id)
    : parsedLocalStorageAppointmentListParams?.filter?.location_id ||
      permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.impersonate_company_id
    ? []
    : user?.company?.locations?.map((location: any) => location.id);

  const canceledFilter = parsedLocalStorageAppointmentListParams?.filter
    ?.cancelled_at || {
    cancelled_at: {
      equals: null
    }
  };

  const baseFilters =
    dateValue?.[0] && dateValue?.[1]
      ? {
          start_time: {
            gte: dateValue?.[0]?.startOf("day").toISOString()
          },
          end_time: {
            lte: dateValue?.[1]?.endOf("day").toISOString()
          },
          is_blocked_time: {
            equals: false
          },
          ...(permissions.isArtist && {
            appointment_services: {
              is_blocked_time: {
                equals: false
              },
              some: {
                artist: {
                  is: {
                    user_id: user.id
                  }
                }
              }
            }
          }),
          ...canceledFilter
        }
      : {
          ...canceledFilter,
          ...(permissions.isArtist && {
            appointment_services: {
              is_blocked_time: {
                equals: false
              },
              some: {
                artist: {
                  is: {
                    user_id: user.id
                  }
                }
              }
            }
          })
        };

  const filter = isFilteredByLocation
    ? baseFilters
    : {
        ...baseFilters,
        location_id: { in: locationIds }
      };

  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  const handleDatePopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDatePopoverClose = () => {
    setDateAnchorEl(null);
  };

  const datePopoverOpen = Boolean(dateAnchorEl);
  const datePopoverId = datePopoverOpen ? "simple-date-popover" : undefined;

  return (
    <div className="w-full overflow-hidden">
      <CustomList
        filter={filter}
        title={translate("pagenames.appointment")}
        hasCreate={false}
        empty={false}
        sort={{ field: "start_time", order: "DESC" }}
        actions={
          <CustomAppointmentListActions
            title={"Appointments"}
            dateSelector={
              <AppointmentDatesSelectButton
                dateValue={dateValue}
                setDateValue={setDateValue}
                handleDatePopoverClick={handleDatePopoverClick}
              />
            }
            selectToday={() =>
              setDateValue([
                dayjs()?.startOf("day"),
                dayjs().add(7, "day")?.endOf("day")
              ])
            }
          />
        }
      >
        <CustomAppointmentListContent dateValue={dateValue} />
        <Popover
          id={datePopoverId}
          open={datePopoverOpen}
          anchorEl={dateAnchorEl}
          onClose={handleDatePopoverClose}
          anchorOrigin={{
            vertical: "center",
            horizontal: "center"
          }}
          transformOrigin={{
            vertical: -25,
            horizontal: "center"
          }}
        >
          <AppointmentDatesSelect
            dateValue={dateValue}
            setDateValue={setDateValue}
            handleDatePopoverClose={handleDatePopoverClose}
          />
        </Popover>
      </CustomList>
    </div>
  );
}

export const AppointmentArtistField = ({
  appointment_service_id,
  type,
  link = false
}: {
  appointment_service_id: string;
  type: FunctionFieldTypes;
  link?: LinkToType<RaRecord<Identifier>> | undefined;
}) => {
  const { data: appointment_service } = useGetOne("AppointmentService", {
    id: appointment_service_id
  });

  if (!appointment_service) {
    return null;
  }

  if (type === FunctionFieldTypes.SERVICE) {
    return (
      <ReferenceField
        source="service_id"
        reference="Service"
        record={appointment_service}
        key={appointment_service}
        link={link}
      >
        <TextField source="name" />
      </ReferenceField>
    );
  }

  return (
    <ReferenceField
      source="artist_id"
      reference="Artist"
      record={appointment_service}
      key={appointment_service}
      link={link}
    >
      <TextField source="user.first_name" />{" "}
      <TextField source="user.last_name" />
    </ReferenceField>
  );
};
